import { PayloadAction, createAsyncThunk, createEntityAdapter, createSelector, createSlice } from "@reduxjs/toolkit";
import {
  IProjectIntegrationsClient,
  ProjectIntegrationHistoryStatus,
  ProjectIntegrationsClient,
  WalrExportDataCommand,
} from "app/ApiClient";
import { WalrIntegrationHistoryState, WalrIntegrationHistory } from "./walrIntegrationHistoryState";
import { pushIntegrationAlertMessage, refetchIntegrationIfRecordExists } from "slices/projectIntegrations/projectIntegrationsSlice";
import { LoadingState } from "types";
import { getWalrPlatformLink } from "utils/platformLinkHelper";

const projectIntegrationClient: IProjectIntegrationsClient = new ProjectIntegrationsClient(
  process.env.REACT_APP_API_URL,
);

export const exportWalrData = createAsyncThunk(
  "walrIntegrations/ExportWalrData",
  async (data: WalrExportDataCommand & { signal?: AbortSignal, walrDatasetId: string, projectId: number }, { dispatch, rejectWithValue }) => {
    try {
      const { signal, walrDatasetId, ...commandData } = data;
      const result = await projectIntegrationClient.exportWalrData({ ...commandData });
      if (!signal?.aborted)
        dispatch(getWalrStatus({ walrProjectId: result.walrProjectId, token: result.token, integrationId: data.integrationId ?? 0, signal, projectId: data.projectId, walrDatasetId }));
      return result;
    }
    catch (error) {
      rejectWithValue(error);
    }
  })

export const getWalrStatus = createAsyncThunk(
  "walrIntegrations/GetWalrDataExportStatus",
  async ({ walrProjectId, token, integrationId, walrDatasetId, projectId, signal }: { walrProjectId: string, token: string, integrationId: number, walrDatasetId: string, projectId:number, signal?: AbortSignal }, { dispatch, rejectWithValue,requestId }) => {
    try {
      const result = await projectIntegrationClient.getWalrDataExportStatus(token, walrProjectId);
      if (!signal?.aborted && result.status === ProjectIntegrationHistoryStatus.InProgress) {
        setTimeout(() => {
          dispatch(getWalrStatus({ walrProjectId, token, integrationId, walrDatasetId, projectId, signal }))
        }, 5000)
      }
      else if (result.status === ProjectIntegrationHistoryStatus.Complete ){
        dispatch(
        pushIntegrationAlertMessage({
          id: requestId, 
          message: `Data has been pushed to Walr (Walr ID <b>${walrDatasetId}</b>) successfully. You can access the Walr dataset PLATFORM_LINK.`,
          status: LoadingState.succeed, 
          platformLink: getWalrPlatformLink(walrProjectId, walrDatasetId), 
          platformLinkText: "here"
        }));
        dispatch(refetchIntegrationIfRecordExists({ integrationId, projectId}));
      }
      else if(result.status === ProjectIntegrationHistoryStatus.Failed)
      {
        dispatch(pushIntegrationAlertMessage({
          id: requestId, 
          message: `Data cannot be pushed to Walr (Walr ID ${walrDatasetId}). An error occurred: ${(result.message ?? "")}. Please create a HELPDESK_LINK ticket and provide the error message.`,
          status: LoadingState.failed, 
          platformLink: getWalrPlatformLink(walrProjectId, walrDatasetId), 
          platformLinkText: "here"
        }));
      }
      
      return result;
    }
    catch (error) {
      rejectWithValue(error);
    }
  })

const walrIntegrationhistoryAdapter = createEntityAdapter<WalrIntegrationHistory>();

const walrIntegrationsSlice = createSlice({
  name: "walrIntegrationHistory",
  initialState: walrIntegrationhistoryAdapter.getInitialState(),
  reducers: {
    resetWalrIntegration: (state, {payload}: PayloadAction<number>)=> {
      walrIntegrationhistoryAdapter.removeOne(state, payload);
    },
    addInitialRecord: (state, {payload}: PayloadAction<WalrIntegrationHistory>)=>{
      walrIntegrationhistoryAdapter.addOne(state, payload);
    },
  },
  extraReducers: (builder) => {

    builder.addCase(exportWalrData.pending, (state, { meta: { arg: { integrationId } } }) => {
      walrIntegrationhistoryAdapter.upsertOne(state, {
        id: integrationId ?? 0,
        status: ProjectIntegrationHistoryStatus.InProgress,
        token: ""
      })
    });

    builder.addCase(exportWalrData.fulfilled, (state, { payload, meta: { arg: { integrationId } } }) => {
      if (payload) {
        walrIntegrationhistoryAdapter.upsertOne(state, {
          id: integrationId ?? 0,
          status: ProjectIntegrationHistoryStatus.InProgress,
          token: payload?.token ?? "",
          message: payload?.message
        });
      }
    });

    builder.addCase(exportWalrData.rejected, (state, { error, meta: { arg: { integrationId } } }) => {
      walrIntegrationhistoryAdapter.upsertOne(state, {
        id: integrationId ?? 0,
        status: ProjectIntegrationHistoryStatus.Failed,
        token: "",
        message: error.message
      });
    });

    builder.addCase(getWalrStatus.fulfilled, (state, { payload, meta: { arg: { integrationId } } }) => {
      if (payload) {
        walrIntegrationhistoryAdapter.upsertOne(state, {
          id: integrationId ?? 0,
          message: payload.message,
          token: "",
          status: payload?.status
        });
        
      }

    });
  }
});


const { selectById } = walrIntegrationhistoryAdapter.getSelectors();

const getState = (rootState: {
  walrIntegrationHistory: WalrIntegrationHistoryState
}): WalrIntegrationHistoryState => rootState.walrIntegrationHistory;

export const selectWalrIntegrationHistory = createSelector(getState, state => (id: number) => selectById(state, id));

export const { resetWalrIntegration, addInitialRecord } = walrIntegrationsSlice.actions;

export default walrIntegrationsSlice.reducer; 
